import { Link } from 'react-router-dom';
import { amountColumn, textColumn, type DataTableColumn } from '@odo/shared-ui';
import type { BillingInvoiceListItem } from '../../../services/billing';
import { formatMoneyDollars as money } from '../../../utils/money';
import { PayButton } from '../shared/PayButton';
import { StatusBadge } from '../shared/StatusBadge';

// Column definitions for the doctor dashboard's invoices table, built on
// @odo/shared-ui's DataTable framework — mirrors files-viewer's
// billing/invoices/invoiceColumns.tsx (no Practice/Charge columns; single-
// practice-scoped context, and a Pay action in place of the row kebab menu).
export type InvoicesSortBy = 'due' | 'amount' | 'status' | 'patient' | 'created';

function fmtDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

const DAYS_PER_MONTH = 30; // approximate, for compact display only
const DAYS_PER_YEAR = 365;
const DAYS_LATE_MONTH_THRESHOLD = 60; // ~2 months — below this, show days

// Compact "Nd/Nmo/Ny late" text for the Due column — days below ~2 months,
// whole months (floored) from there up to a year, then whole years plus a
// leftover-months remainder past that. All-floor, no rounding up, so it
// never overstates lateness.
function formatDaysLate(daysLate: number): string {
    if (daysLate < DAYS_LATE_MONTH_THRESHOLD) {
        return `${daysLate}d late`;
    }
    if (daysLate < DAYS_PER_YEAR) {
        return `${Math.floor(daysLate / DAYS_PER_MONTH)}mo late`;
    }
    const years = Math.floor(daysLate / DAYS_PER_YEAR);
    const months = Math.floor((daysLate % DAYS_PER_YEAR) / DAYS_PER_MONTH);
    return months > 0 ? `${years}y ${months}mo late` : `${years}y late`;
}

function isOverdueRow(invoice: BillingInvoiceListItem): boolean {
    return invoice.derivedStatus === 'overdue';
}

// Percentage widths (fixed table layout, summing to 100%) keep the narrow
// fixed-content columns compact while Patient absorbs the most space, capped
// so a long name can't dominate the row.
export function buildInvoiceColumns(): DataTableColumn<BillingInvoiceListItem, InvoicesSortBy>[] {
    return [
        textColumn<BillingInvoiceListItem, InvoicesSortBy>({
            key: 'invoiceNumber',
            label: 'Invoice',
            width: '12%',
            truncate: false,
            className: 'font-mono text-[12px] text-gray-700',
            headerClassName: 'whitespace-nowrap px-3 pl-6',
            cellClassName: 'pl-6 pr-3 whitespace-nowrap',
            value: (invoice) => invoice.invoiceNumber
        }),
        {
            key: 'patient',
            label: 'Patient',
            width: '22%',
            sortable: true,
            sortKey: 'patient',
            truncate: true,
            headerClassName: 'whitespace-nowrap px-3',
            cellClassName: 'px-3 text-sm max-w-0',
            getTitle: (invoice) => invoice.charge.patientName ?? undefined,
            render: (invoice) =>
                invoice.charge.patientId ? (
                    <Link
                        to={`/patient/${invoice.charge.patientId}/overview`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-block max-w-full align-middle truncate text-gray-700 hover:underline hover:text-gray-900 transition-colors"
                    >
                        {invoice.charge.patientName ?? 'Unknown patient'}
                    </Link>
                ) : (
                    <span className="text-gray-300">-</span>
                )
        },
        textColumn<BillingInvoiceListItem, InvoicesSortBy>({
            key: 'installments',
            label: 'Inst.',
            width: '8%',
            truncate: false,
            headerClassName: 'whitespace-nowrap px-4',
            cellClassName: 'px-4 text-sm text-gray-500 whitespace-nowrap',
            value: (invoice) => `${invoice.installmentIndex + 1} of ${invoice.installmentCount}`
        }),
        amountColumn<BillingInvoiceListItem, InvoicesSortBy>({
            key: 'amount',
            label: 'Amount',
            width: '12%',
            sortable: true,
            sortKey: 'amount',
            className: 'text-sm font-semibold text-gray-900 tabular-nums',
            headerClassName: 'whitespace-nowrap px-4',
            cellClassName: 'px-4 whitespace-nowrap',
            formatter: (amount) => money(amount),
            value: (invoice) => Number(invoice.amount)
        }),
        textColumn<BillingInvoiceListItem, InvoicesSortBy>({
            key: 'created',
            label: 'Created',
            width: '12%',
            sortable: true,
            sortKey: 'created',
            truncate: false,
            className: 'text-sm text-gray-500',
            headerClassName: 'whitespace-nowrap px-4',
            cellClassName: 'px-4 whitespace-nowrap',
            value: (invoice) => fmtDate(invoice.createdAt)
        }),
        {
            key: 'due',
            label: 'Due',
            width: '14%',
            sortable: true,
            sortKey: 'due',
            headerClassName: 'whitespace-nowrap px-4',
            cellClassName: 'px-4 text-sm whitespace-nowrap',
            render: (invoice) => {
                const overdue = isOverdueRow(invoice);
                return (
                    <span className={overdue ? 'text-red-600 font-medium' : 'text-gray-500'}>
                        {fmtDate(invoice.dueDate)}
                        {overdue && invoice.daysLate !== null && (
                            <span className="block text-xs font-semibold">
                                {formatDaysLate(invoice.daysLate)}
                            </span>
                        )}
                    </span>
                );
            }
        },
        {
            key: 'status',
            label: 'Status',
            width: '10%',
            align: 'center',
            sortable: true,
            sortKey: 'status',
            truncate: false,
            headerClassName: 'whitespace-nowrap px-4',
            cellClassName: 'px-4',
            render: (invoice) => <StatusBadge status={invoice.derivedStatus} />
        },
        {
            key: 'pay',
            label: '',
            width: '10%',
            align: 'center',
            truncate: false,
            stopClickPropagation: true,
            headerClassName: 'whitespace-nowrap px-4',
            cellClassName: 'px-4',
            render: (invoice) => (
                <PayButton invoiceId={invoice.id} derivedStatus={invoice.derivedStatus} />
            )
        }
    ];
}
